import type { TargetPriority } from '../config/constants';
import { scaleSupplyCost, scaleUpgradeCost } from '../config/constants';
import { TOWERS, type TowerDef, type TowerStageDef } from '../data/towers';
import { SYMBIOSIS, type SymbiosisDef } from '../data/symbiosis';
import type { Enemy } from './Enemy';
import { nextEntityId } from './Enemy';

export interface TowerBonuses {
  damageMul: number;
  rangeMul: number;
  rateMul: number;
  armourPenAdd: number;
  symbiosisDamageMul: number;
  swapCreditBonus: number;
}

/** Kill milestones for the three battlefield veterancy stars. */
export const VETERANCY_THRESHOLDS = [10, 30, 75] as const;

export function veterancyStars(kills: number): number {
  return VETERANCY_THRESHOLDS.reduce((stars, threshold) => stars + (kills >= threshold ? 1 : 0), 0);
}

export function formatVeterancy(kills: number): string {
  const stars = veterancyStars(kills);
  return `${'★'.repeat(stars)}${'☆'.repeat(3 - stars)}`;
}

const NO_BONUS: TowerBonuses = { damageMul: 1, rangeMul: 1, rateMul: 1, armourPenAdd: 0, symbiosisDamageMul: 1, swapCreditBonus: 0 };

export class Tower {
  id: number;
  def: TowerDef;
  stageIndex = 0;
  padId: number;
  x: number;
  y: number;
  cooldown = 0;
  specialCooldown = 0;
  specialActive = 0;
  priority: TargetPriority = 'FIRST';
  angle = 0;
  targetAngle = 0;
  kills = 0;
  damageDealt = 0;
  jammed = 0;
  flash = 0;
  bonus: TowerBonuses = NO_BONUS;
  /** Symbiosis module bolted onto the tower (available at EXPERIMENTAL). */
  secondary: string | null = null;
  /** Bolt-on tier: 1 = installed, 3 = fully tuned. */
  secondaryLevel = 0;
  /** Supply invested in the current bolt-on, including any tuning upgrades. */
  secondaryInvested = 0;
  secondaryCooldown = 0;
  secondaryAngle = 0;
  secondaryTargetAngle = 0;
  /** Live target point for a continuous laser beam (null = not firing). */
  secondaryBeam: { x: number; y: number } | null = null;
  secondaryFlash = 0;
  /** Fire-rate bonus granted by neighbouring Shield Emitters. */
  auraRateBonus = 0;
  /** Persistent damage from ranged hostiles. Towers auto-repair after a short quiet period. */
  maxHp = 100;
  hp = 100;
  damageCooldown = 0;
  disabledT = 0;

  constructor(towerId: string, padId: number, x: number, y: number, bonus?: TowerBonuses) {
    this.id = nextEntityId();
    this.def = TOWERS[towerId];
    this.padId = padId;
    this.x = x;
    this.y = y;
    if (bonus) this.bonus = bonus;
  }

  get stage(): TowerStageDef {
    return this.def.stages[this.stageIndex];
  }

  get maxStage(): number {
    return this.def.stages.length - 1;
  }

  get isMax(): boolean {
    return this.stageIndex >= this.maxStage;
  }

  get upgradeCost(): number | null {
    if (this.isMax) return null;
    return scaleUpgradeCost(this.def.stages[this.stageIndex + 1].cost);
  }

  get sellValue(): number {
    let spent = 0;
    for (let i = 0; i <= this.stageIndex; i++) {
      spent += i === 0 ? scaleSupplyCost(this.def.stages[i].cost) : scaleUpgradeCost(this.def.stages[i].cost);
    }
    return Math.floor(spent * 0.55);
  }

  get range(): number {
    const s = this.stage;
    let mul = this.specialActive > 0 ? 1.05 : 1;
    if (this.secondaryDef?.buffRange) mul *= 1 + this.secondaryDef.buffRange;
    return s.range * mul * this.bonus.rangeMul;
  }

  get fireInterval(): number {
    const s = this.stage;
    let rate = s.fireRate * (this.jammed > 0 ? 0.45 : 1) * this.bonus.rateMul;
    if (this.secondaryDef?.buffRate) rate *= 1 + this.secondaryDef.buffRate;
    rate *= 1 + this.auraRateBonus + this.veterancyStars * 0.015;
    if (this.specialActive > 0) rate *= 1.65;
    return 1 / Math.max(0.05, rate);
  }

  get secondaryDef(): SymbiosisDef | null {
    return this.secondary ? SYMBIOSIS[this.secondary] ?? null : null;
  }

  get hasSecondary(): boolean {
    return this.secondaryDef != null;
  }

  get healthPercent(): number {
    return this.maxHp <= 0 ? 0 : this.hp / this.maxHp;
  }

  get isDisabled(): boolean {
    return this.disabledT > 0;
  }

  takeDamage(amount: number): void {
    this.hp = Math.max(0, this.hp - Math.max(0, amount));
    this.damageCooldown = 3.5;
    if (this.hp <= 0) this.disabledT = Math.max(this.disabledT, 4);
    this.flash = Math.max(this.flash, 0.18);
  }

  repair(amount: number): void {
    this.hp = Math.min(this.maxHp, this.hp + Math.max(0, amount));
    if (this.hp > 0) this.disabledT = 0;
  }

  get secondaryRange(): number {
    const d = this.secondaryDef;
    return d ? d.range * (1 + Math.max(0, this.secondaryLevel - 1) * 0.1) * this.bonus.rangeMul : 0;
  }

  get secondaryFireInterval(): number {
    const d = this.secondaryDef;
    if (!d || d.fireRate <= 0) return Infinity;
    const rate = d.fireRate * (1 + Math.max(0, this.secondaryLevel - 1) * 0.12) * this.bonus.rateMul * (1 + this.auraRateBonus + this.veterancyStars * 0.015);
    return 1 / Math.max(0.05, rate);
  }

  get secondaryDamage(): number {
    const d = this.secondaryDef;
    return d ? d.damage * (1 + Math.max(0, this.secondaryLevel - 1) * 0.2) * this.bonus.damageMul * this.bonus.symbiosisDamageMul * (1 + this.veterancyStars * 0.03) : 0;
  }

  /** Swap the primary weapon to an evolved form, keeping the current stage. */
  evolveTo(id: string): boolean {
    const target = TOWERS[id];
    if (!target) return false;
    this.def = target;
    this.stageIndex = Math.min(this.stageIndex, target.stages.length - 1);
    this.flash = 0.5;
    return true;
  }

  attachSecondary(id: string): boolean {
    const def = SYMBIOSIS[id];
    if (!def) return false;
    this.secondary = id;
    this.secondaryLevel = 1;
    this.secondaryInvested = scaleSupplyCost(def.cost);
    this.secondaryCooldown = 0;
    this.secondaryFlash = 0.4;
    return true;
  }

  /**
   * Credit applied when replacing the current bolt-on with another module.
   * Tuning is part of the old module's value, so it is included in the refund.
   */
  secondarySwapCredit(targetId: string): number {
    if (!this.secondaryDef || this.secondary === targetId) return 0;
    const invested = this.secondaryInvested > 0 ? this.secondaryInvested : scaleSupplyCost(this.secondaryDef.cost);
    return Math.floor(invested * Math.min(0.9, 0.75 + this.bonus.swapCreditBonus));
  }

  /** Net supply change for installing a module (negative means a surplus credit). */
  secondarySwapCost(targetId: string): number | null {
    const target = SYMBIOSIS[targetId];
    if (!target) return null;
    return scaleSupplyCost(target.cost) - this.secondarySwapCredit(targetId);
  }

  get secondaryUpgradeCost(): number | null {
    if (!this.secondaryDef || this.secondaryLevel >= 3) return null;
    return scaleUpgradeCost(this.secondaryDef.cost * (0.75 + this.secondaryLevel * 0.35));
  }

  upgradeSecondary(): boolean {
    if (!this.secondaryDef || this.secondaryLevel >= 3) return false;
    const cost = this.secondaryUpgradeCost;
    if (cost == null) return false;
    this.secondaryInvested += cost;
    this.secondaryLevel++;
    this.secondaryFlash = 0.4;
    return true;
  }

  get damage(): number {
    const s = this.stage;
    let d = s.damage * (this.jammed > 0 ? 0.7 : 1) * this.bonus.damageMul * (1 + veterancyStars(this.kills) * 0.03);
    if (this.specialActive > 0) d *= s.specialDamageMul ?? 1.3;
    return d;
  }

  get armourPen(): number {
    return Math.min(0.9, (this.stage.armourPen ?? 0) + this.bonus.armourPenAdd);
  }

  get veterancyStars(): number {
    return veterancyStars(this.kills);
  }

  canSpecial(): boolean {
    return this.isMax && this.specialCooldown <= 0 && !!this.stage.specialName;
  }

  activateSpecial(): boolean {
    if (!this.canSpecial()) return false;
    this.specialCooldown = this.stage.specialCooldown ?? 60;
    this.specialActive = this.stage.specialDuration ?? 3;
    return true;
  }

  upgrade(): boolean {
    if (this.isMax) return false;
    this.stageIndex++;
    this.flash = 0.4;
    return true;
  }

  pickTarget(enemies: Enemy[]): Enemy | null {
    const s = this.stage;
    const inRange: Enemy[] = [];
    const r2 = this.range * this.range;
    for (const e of enemies) {
      if (!e.alive || e.distDone) continue;
      const dx = e.x - this.x;
      const dy = e.y - this.y;
      if (dx * dx + dy * dy <= r2) inRange.push(e);
    }
    if (!inRange.length) return null;

    switch (this.priority) {
      case 'FIRST':
        inRange.sort((a, b) => b.dist - a.dist);
        break;
      case 'LAST':
        inRange.sort((a, b) => a.dist - b.dist);
        break;
      case 'STRONGEST':
        inRange.sort((a, b) => b.hp - a.hp);
        break;
      case 'WEAKEST':
        inRange.sort((a, b) => a.hp - b.hp);
        break;
      case 'FASTEST':
        inRange.sort((a, b) => b.def.speed - a.def.speed);
        break;
      case 'ARMOURED':
        inRange.sort((a, b) => b.def.armour - a.def.armour);
        break;
      case 'SPECIAL':
        inRange.sort((a, b) => {
          const sa = (a.isElite ? 2 : 0) + (a.isBoss ? 2 : 0) + (a.def.spawnChild ? 1 : 0) + (a.def.stunTowers ? 1 : 0);
          const sb = (b.isElite ? 2 : 0) + (b.isBoss ? 2 : 0) + (b.def.spawnChild ? 1 : 0) + (b.def.stunTowers ? 1 : 0);
          return sb - sa;
        });
        break;
    }
    return inRange[0] ?? null;
  }

  update(dt: number): void {
    if (this.cooldown > 0) this.cooldown -= dt;
    if (this.specialCooldown > 0) this.specialCooldown -= dt;
    if (this.specialActive > 0) this.specialActive -= dt;
    if (this.jammed > 0) this.jammed -= dt;
    if (this.flash > 0) this.flash -= dt;
    if (this.secondaryCooldown > 0) this.secondaryCooldown -= dt;
    if (this.secondaryFlash > 0) this.secondaryFlash -= dt;
    if (this.damageCooldown > 0) this.damageCooldown -= dt;
    else if (this.hp < this.maxHp) this.hp = Math.min(this.maxHp, this.hp + dt * 4);
    if (this.disabledT > 0) this.disabledT -= dt;
    // Smooth barrel turn
    let diff = this.targetAngle - this.angle;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    this.angle += diff * Math.min(1, dt * 8);
    // Secondary turret tracks a little slower than the main gun.
    let sdiff = this.secondaryTargetAngle - this.secondaryAngle;
    while (sdiff > Math.PI) sdiff -= Math.PI * 2;
    while (sdiff < -Math.PI) sdiff += Math.PI * 2;
    this.secondaryAngle += sdiff * Math.min(1, dt * 6);
  }
}
